import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SurveyListHeader from '../../../../../component/common/layout/SurveyListHeader';
import SurveyListSidebar from '../../../../../component/common/layout/SurveyListSidebar';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useVerifySurveyId } from '../../../../../hooks';
import { getEmailTemplate, upsertEmailTemplate, deleteEmailTemplate, toggleReportEmail } from '../../../../../store/actions/emailTemplateAction';
import { toast } from 'react-toastify';

const LOGO_URL = process.env.REACT_APP_SITE_LOGO || 'https://prod-clearview.s3.ap-south-1.amazonaws.com/local/vf/theme/logo/logo-logo-VF_Business_Logo_RGB_RED%201.png';

const FONT_OPTIONS = [
    'Arial, Helvetica, sans-serif',
    'Verdana, Geneva, sans-serif',
    'Tahoma, Geneva, sans-serif',
    'Georgia, serif',
    '"Times New Roman", Times, serif',
    '"Trebuchet MS", Helvetica, sans-serif',
    '"Courier New", Courier, monospace',
];

const DEFAULT_FIELDS = {
    emailBgColor: '#F6F6F6',
    cardBgColor: '#ffffff',
    fontFamily: 'Arial, Helvetica, sans-serif',
    cardPadding: '25',

    heading: 'Your Report is Ready',
    headingColor: '#E60000',
    headingFontSize: '22',

    description: 'The report you requested has been successfully generated and is ready for view. Click the button below to securely access your report.',
    descriptionFontSize: '14',
    descriptionColor: '#494C4E',

    buttonText: 'View Your Report',
    buttonBg: '#E60000',
    buttonTextColor: '#ffffff',
    buttonRadius: '4',
    buttonPaddingV: '14',
    buttonPaddingH: '36',
    buttonFontSize: '16',
    buttonSectionBg: '#f5f5f5',

    securityNote: 'For security reasons, this view link may expire after a limited time.',
    securityNoteFontSize: '12',
    securityNoteColor: '#494C4E',

    assistanceNote: 'If you did not request this report or need assistance, please contact our support team.',
    assistanceNoteFontSize: '12',
    assistanceNoteColor: '#494C4E',

    signoff: 'Thank you,\nThe {{organizationName}} Team',
    signoffFontSize: '12',
    signoffColor: '#494C4E',

    footer: 'If you experience any issues viewing your report, please contact our support team immediately at {{supportEmail}}',
    footerFontSize: '12',
    footerColor: '#494C4E',
    footerBorderColor: '#C1C7CF',
};

const fieldStyle = {
    border: '1px dashed #ccc',
    borderRadius: '3px',
    background: 'transparent',
    padding: '6px 8px',
    width: '100%',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    color: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    resize: 'vertical',
    outline: 'none',
    boxSizing: 'border-box',
};

const panelLabel = { fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '4px', display: 'block' };
const panelRow = { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' };
const colorInput = { width: '32px', height: '32px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', padding: '1px', flexShrink: 0 };
const rangeWrap = { display: 'flex', alignItems: 'center', gap: '6px', flex: 1 };
const rangeVal = { fontSize: '13px', color: '#555', minWidth: '32px', textAlign: 'right' };

function AccordionSection({ title, open, onToggle, children }) {
    return (
        <div style={{ borderBottom: '1px solid #e9ecef', background: open ? '#fff5f5' : '#fff', transition: 'background 0.2s' }}>
            <button
                onClick={onToggle}
                style={{
                    width: '100%', background: 'none', border: 'none', padding: '12px 14px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: open ? '#E60000' : '#333',
                }}
            >
                {title}
                <span style={{ fontSize: '16px', color: open ? '#E60000' : '#999', transition: 'transform 0.2s, color 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>&#9660;</span>
            </button>
            {open && <div style={{ padding: '0 14px 14px' }}>{children}</div>}
        </div>
    );
}

function ColorRow({ label, value, onChange, canEdit }) {
    const ro = !canEdit;
    return (
        <div style={panelRow}>
            <span style={{ ...panelLabel, marginBottom: 0, minWidth: '60px' }}>{label}</span>
            <input type="color" value={value} onChange={canEdit ? (e) => onChange(e.target.value) : undefined} disabled={ro} style={{ ...colorInput, cursor: ro ? 'not-allowed' : 'pointer', opacity: ro ? 0.65 : 1 }} />
            <input
                type="text" value={value} readOnly={ro} onChange={canEdit ? (e) => onChange(e.target.value) : undefined}
                style={{ flex: 1, fontSize: '13px', padding: '5px 8px', border: '1px solid #ddd', borderRadius: '3px', fontFamily: 'monospace', background: ro ? '#f5f5f5' : '#fff' }}
            />
        </div>
    );
}

function SliderRow({ label, value, onChange, min, max, unit = 'px', canEdit }) {
    const ro = !canEdit;
    return (
        <div style={panelRow}>
            <span style={{ ...panelLabel, marginBottom: 0, minWidth: '60px' }}>{label}</span>
            <div style={rangeWrap}>
                <input type="range" min={min} max={max} value={value} onChange={canEdit ? (e) => onChange(e.target.value) : undefined} disabled={ro} style={{ flex: 1, opacity: ro ? 0.65 : 1 }} />
                <span style={rangeVal}>{value}{unit}</span>
            </div>
        </div>
    );
}

export default function EmailTemplateEditor() {
    const { t } = useTranslation();
    const [subject, setSubject] = useState('Your Report is Ready');
    const [fields, setFields] = useState({ ...DEFAULT_FIELDS });
    const [saving, setSaving] = useState(false);
    const [ready, setReady] = useState(false);
    const [isEnabled, setIsEnabled] = useState(true);
    const [openSections, setOpenSections] = useState({ general: true, heading: false, description: false, button: false, notes: false, signoff: false, footer: false, placeholders: true });
    const [showResetModal, setShowResetModal] = useState(false);

    const dispatch = useDispatch();
    const survey_id = useVerifySurveyId();
    const { survey_name } = useSelector((state) => state.permissionsData);
    const { templateData, loading, toggling } = useSelector((state) => state.emailTemplateData);

    const canEdit = window.modulePermissions.M14.edit == 1;

    const placeholders = [
        { key: 'organizationName', label: '{{organizationName}}', desc: t('survey.email_template.placeholder_org_name') },
        { key: 'supportEmail', label: '{{supportEmail}}', desc: t('survey.email_template.placeholder_support_email') },
        { key: 'reportUrl', label: '{{reportUrl}}', desc: t('survey.email_template.placeholder_report_url') },
    ];

    useEffect(() => {
        if (survey_id) {
            setReady(false);
            dispatch(getEmailTemplate({ survey_id }));
        }
    }, [survey_id, dispatch]);

    useEffect(() => {
        if (loading || ready) return;
        if (templateData) {
            setIsEnabled(templateData.email_enabled !== 0);
            if (templateData.content) {
                try {
                    const parsed = JSON.parse(templateData.content);
                    if (parsed && parsed.heading) {
                        setFields({ ...DEFAULT_FIELDS, ...parsed });
                    }
                } catch (e) {
                    setFields({ ...DEFAULT_FIELDS });
                }
                setSubject(templateData.subject || 'Your Report is Ready');
            } else {
                setFields({ ...DEFAULT_FIELDS });
                setSubject('Your Report is Ready');
            }
        } else {
            setIsEnabled(true);
            setFields({ ...DEFAULT_FIELDS });
            setSubject('Your Report is Ready');
        }
        setReady(true);
    }, [loading, templateData, ready]);

    const handleToggle = () => {
        if (!survey_id) return;
        const newVal = !isEnabled;
        setIsEnabled(newVal);
        dispatch(toggleReportEmail(survey_id, newVal, () => {
            toast.success(newVal ? t('survey.email_template.toast_enabled') : t('survey.email_template.toast_disabled'));
        }));
    };

    const updateField = (key, value) => {
        if (!canEdit) return;
        setFields(prev => ({ ...prev, [key]: value }));
    };

    const toggleSection = (key) => {
        setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const copyPlaceholder = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            toast.info(t('survey.email_template.toast_copied', { text }), { autoClose: 2000 });
        }).catch(() => {
            toast.info(t('survey.email_template.toast_copy_fallback', { text }), { autoClose: 3000 });
        });
    };

    const handleSave = useCallback(() => {
        if (!survey_id || !canEdit) return;
        setSaving(true);
        dispatch(upsertEmailTemplate(
            { survey_id, subject, content: JSON.stringify(fields), css: '' },
            () => {
                setSaving(false);
                toast.success(t('survey.email_template.toast_saved'));
            }
        ));
        setTimeout(() => setSaving(false), 3000);
    }, [survey_id, subject, fields, dispatch, t, canEdit]);

    const handleResetConfirm = useCallback(() => {
        if (!survey_id || !canEdit) return;
        dispatch(deleteEmailTemplate({ survey_id }, () => {
            setFields({ ...DEFAULT_FIELDS });
            setSubject('Your Report is Ready');
            setShowResetModal(false);
            toast.success(t('survey.email_template.toast_reverted'));
        }));
    }, [survey_id, dispatch, t, canEdit]);

    const ff = fields.fontFamily || 'Arial, Helvetica, sans-serif';
    const cp = Number(fields.cardPadding) || 25;

    return (
        <div className='d-flex flex-column main-outer-wrapper main-site-wrapper'>
            <SurveyListHeader />
            <div className="flex-grow-1 d-flex survey-main-wrapper">
                <SurveyListSidebar />
                <div id="content" className="flex-grow-1 dashboard-page-outer d-flex flex-column">
                    <div style={{ padding: '16px 24px', borderBottom: '1px solid #e9ecef', background: '#fff', flexShrink: 0 }}>
                        <div className="d-flex align-items-center justify-content-between">
                            <div>
                                <nav aria-label="breadcrumb">
                                    <ol className="breadcrumb" style={{ marginBottom: '4px' }}>
                                        <li className="breadcrumb-item">
                                            <Link to="/admin/survey/list">{t('survey.title')}</Link>
                                        </li>
                                        <li className="breadcrumb-item">
                                            <Link to={`/admin/survey/create/qa-builder?survey_id=${survey_id}`}>{survey_name}</Link>
                                        </li>
                                        <li className="breadcrumb-item active" aria-current="page"><strong>{t('survey.email_template.breadcrumb')}</strong></li>
                                    </ol>
                                </nav>
                                <h2 style={{ margin: 0, fontSize: '18px' }}>{t('survey.email_template.page_title')}</h2>
                            </div>
                            <div className="d-flex align-items-center" style={{ gap: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: 0 }}>
                                    <div
                                        onClick={canEdit ? (toggling ? undefined : handleToggle) : undefined}
                                        style={{
                                            width: '48px', height: '26px', borderRadius: '13px', cursor: (!canEdit || toggling) ? 'not-allowed' : 'pointer',
                                            background: isEnabled ? '#E60000' : '#ccc', position: 'relative', transition: 'background 0.25s',
                                        }}
                                    >
                                        <div style={{
                                            width: '22px', height: '22px', borderRadius: '50%', background: '#fff',
                                            position: 'absolute', top: '2px', left: isEnabled ? '24px' : '2px',
                                            transition: 'left 0.25s', boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                                        }} />
                                    </div>
                                    <span style={{ fontWeight: 500, fontSize: '13px', color: isEnabled ? '#E60000' : '#999', userSelect: 'none' }}>
                                        {isEnabled ? t('survey.email_template.email_enabled') : t('survey.email_template.email_disabled')}
                                    </span>
                                </div>
                                {canEdit && (<div className="button-group-custom" style={{ marginLeft: '8px' }}>
                                    <button className='btn btn-bordered color-cancel' onClick={() => setShowResetModal(true)}>{t('survey.email_template.reset_to_default')}</button>
                                    <button className='btn btn-primary add-btn ms-3' onClick={handleSave} disabled={saving}>
                                        {saving ? t('survey.email_template.saving') : t('survey.email_template.save_template')}
                                    </button>
                                </div>)}
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

                        {/* LEFT: Email Preview */}
                        <div style={{ flex: '7', overflow: 'auto', background: '#e9ecef' }}>
                            <div style={{ padding: '30px 20px', minHeight: '100%' }}>
                                <div style={{ background: fields.emailBgColor || '#F6F6F6', padding: '40px 20px', borderRadius: '6px', border: '1px solid #ddd' }}>
                                    <div style={{
                                        maxWidth: '600px', margin: '0 auto', padding: `${cp}px`,
                                        background: fields.cardBgColor || '#ffffff', borderRadius: '4px',
                                        fontFamily: ff, color: '#25282B',
                                    }}>
                                        <div style={{ paddingBottom: '40px' }}>
                                            <img src={LOGO_URL} alt="Logo" style={{ maxWidth: '210px' }} />
                                        </div>

                                        <div style={{ paddingBottom: '20px' }}>
                                            <input
                                                type="text"
                                                value={fields.heading}
                                                readOnly={!canEdit}
                                                onChange={(e) => updateField('heading', e.target.value)}
                                                style={{
                                                    ...fieldStyle, fontFamily: ff,
                                                    fontSize: `${fields.headingFontSize || 22}px`,
                                                    fontWeight: 'bold',
                                                    color: fields.headingColor || '#E60000',
                                                    cursor: canEdit ? 'text' : 'default',
                                                    background: canEdit ? 'transparent' : '#f5f5f5',
                                                }}
                                            />
                                        </div>

                                        <div style={{ paddingBottom: '25px' }}>
                                            <textarea
                                                value={fields.description}
                                                readOnly={!canEdit}
                                                onChange={(e) => updateField('description', e.target.value)}
                                                rows={3}
                                                style={{
                                                    ...fieldStyle, fontFamily: ff,
                                                    fontSize: `${fields.descriptionFontSize || 14}px`,
                                                    color: fields.descriptionColor || '#494C4E',
                                                    lineHeight: '1.6',
                                                    background: canEdit ? 'transparent' : '#f5f5f5',
                                                }}
                                            />
                                        </div>

                                        <div style={{
                                            background: fields.buttonSectionBg || '#f5f5f5',
                                            padding: '30px 19px', textAlign: 'center', borderRadius: '4px',
                                        }}>
                                            <input
                                                type="text"
                                                value={fields.buttonText}
                                                readOnly={!canEdit}
                                                onChange={(e) => updateField('buttonText', e.target.value)}
                                                style={{
                                                    display: 'inline-block',
                                                    border: '1px dashed rgba(255,255,255,0.4)',
                                                    background: fields.buttonBg || '#E60000',
                                                    borderRadius: `${fields.buttonRadius || 4}px`,
                                                    padding: `${fields.buttonPaddingV || 14}px ${fields.buttonPaddingH || 36}px`,
                                                    fontSize: `${fields.buttonFontSize || 16}px`,
                                                    fontWeight: '700',
                                                    color: fields.buttonTextColor || '#ffffff',
                                                    textAlign: 'center',
                                                    outline: 'none',
                                                    fontFamily: ff,
                                                    cursor: canEdit ? 'text' : 'default',
                                                }}
                                            />
                                        </div>

                                        <div style={{ paddingTop: '18px', paddingBottom: '4px' }}>
                                            <textarea
                                                value={fields.securityNote}
                                                readOnly={!canEdit}
                                                onChange={(e) => updateField('securityNote', e.target.value)}
                                                rows={2}
                                                style={{
                                                    ...fieldStyle, fontFamily: ff,
                                                    fontSize: `${fields.securityNoteFontSize || 12}px`,
                                                    color: fields.securityNoteColor || '#494C4E',
                                                    background: canEdit ? 'transparent' : '#f5f5f5',
                                                }}
                                            />
                                        </div>

                                        <div style={{ paddingBottom: '30px' }}>
                                            <textarea
                                                value={fields.assistanceNote}
                                                readOnly={!canEdit}
                                                onChange={(e) => updateField('assistanceNote', e.target.value)}
                                                rows={2}
                                                style={{
                                                    ...fieldStyle, fontFamily: ff,
                                                    fontSize: `${fields.assistanceNoteFontSize || 12}px`,
                                                    color: fields.assistanceNoteColor || '#494C4E',
                                                    background: canEdit ? 'transparent' : '#f5f5f5',
                                                }}
                                            />
                                        </div>

                                        <div style={{ paddingBottom: '35px' }}>
                                            <textarea
                                                value={fields.signoff}
                                                readOnly={!canEdit}
                                                onChange={(e) => updateField('signoff', e.target.value)}
                                                rows={2}
                                                style={{
                                                    ...fieldStyle, fontFamily: ff,
                                                    fontSize: `${fields.signoffFontSize || 12}px`,
                                                    color: fields.signoffColor || '#494C4E',
                                                    background: canEdit ? 'transparent' : '#f5f5f5',
                                                }}
                                            />
                                        </div>

                                        <div style={{ borderTop: `1px solid ${fields.footerBorderColor || '#C1C7CF'}`, paddingTop: '10px' }}>
                                            <textarea
                                                value={fields.footer}
                                                readOnly={!canEdit}
                                                onChange={(e) => updateField('footer', e.target.value)}
                                                rows={2}
                                                style={{
                                                    ...fieldStyle, fontFamily: ff,
                                                    fontSize: `${fields.footerFontSize || 12}px`,
                                                    color: fields.footerColor || '#494C4E',
                                                    background: canEdit ? 'transparent' : '#f5f5f5',
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT: Style Panel */}
                        <div style={{
                            flex: '3', overflow: 'auto', background: '#fff',
                            borderLeft: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column',
                        }}>
                            <div style={{ padding: '14px', borderBottom: '1px solid #e9ecef' }}>
                                <label style={panelLabel}>{t('survey.email_template.email_subject')}</label>
                                <input
                                    type="text"
                                    value={subject}
                                    readOnly={!canEdit}
                                    onChange={(e) => canEdit && setSubject(e.target.value)}
                                    placeholder={t('survey.email_template.subject_placeholder')}
                                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', background: canEdit ? '#fff' : '#f5f5f5' }}
                                />
                            </div>

                            <div style={{ flex: 1, overflow: 'auto' }}>
                                <AccordionSection title={t('survey.email_template.section_general')} open={openSections.general} onToggle={() => toggleSection('general')}>
                                    <ColorRow canEdit={canEdit} label={t('survey.email_template.label_email_bg')} value={fields.emailBgColor || '#F6F6F6'} onChange={(v) => updateField('emailBgColor', v)} />
                                    <ColorRow canEdit={canEdit} label={t('survey.email_template.label_card_bg')} value={fields.cardBgColor || '#ffffff'} onChange={(v) => updateField('cardBgColor', v)} />
                                    <div style={panelRow}>
                                        <span style={{ ...panelLabel, marginBottom: 0, minWidth: '60px' }}>{t('survey.email_template.label_font')}</span>
                                        <select
                                            value={fields.fontFamily || 'Arial, Helvetica, sans-serif'}
                                            disabled={!canEdit}
                                            onChange={(e) => updateField('fontFamily', e.target.value)}
                                            style={{ flex: 1, padding: '6px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', background: canEdit ? '#fff' : '#f5f5f5' }}
                                        >
                                            {FONT_OPTIONS.map(f => (
                                                <option key={f} value={f} style={{ fontFamily: f }}>
                                                    {f.split(',')[0].replace(/"/g, '')}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <SliderRow canEdit={canEdit} label={t('survey.email_template.label_padding')} value={fields.cardPadding || '25'} onChange={(v) => updateField('cardPadding', v)} min={10} max={50} />
                                </AccordionSection>

                                <AccordionSection title={t('survey.email_template.section_heading')} open={openSections.heading} onToggle={() => toggleSection('heading')}>
                                    <ColorRow canEdit={canEdit} label={t('survey.email_template.label_color')} value={fields.headingColor || '#E60000'} onChange={(v) => updateField('headingColor', v)} />
                                    <SliderRow canEdit={canEdit} label={t('survey.email_template.label_font_size')} value={fields.headingFontSize || '22'} onChange={(v) => updateField('headingFontSize', v)} min={14} max={40} />
                                </AccordionSection>

                                <AccordionSection title={t('survey.email_template.section_description')} open={openSections.description} onToggle={() => toggleSection('description')}>
                                    <ColorRow canEdit={canEdit} label={t('survey.email_template.label_color')} value={fields.descriptionColor || '#494C4E'} onChange={(v) => updateField('descriptionColor', v)} />
                                    <SliderRow canEdit={canEdit} label={t('survey.email_template.label_font_size')} value={fields.descriptionFontSize || '14'} onChange={(v) => updateField('descriptionFontSize', v)} min={10} max={24} />
                                </AccordionSection>

                                <AccordionSection title={t('survey.email_template.section_button')} open={openSections.button} onToggle={() => toggleSection('button')}>
                                    <ColorRow canEdit={canEdit} label={t('survey.email_template.label_bg_color')} value={fields.buttonBg || '#E60000'} onChange={(v) => updateField('buttonBg', v)} />
                                    <ColorRow canEdit={canEdit} label={t('survey.email_template.label_text_color')} value={fields.buttonTextColor || '#ffffff'} onChange={(v) => updateField('buttonTextColor', v)} />
                                    <ColorRow canEdit={canEdit} label={t('survey.email_template.label_section_bg')} value={fields.buttonSectionBg || '#f5f5f5'} onChange={(v) => updateField('buttonSectionBg', v)} />
                                    <SliderRow canEdit={canEdit} label={t('survey.email_template.label_radius')} value={fields.buttonRadius || '4'} onChange={(v) => updateField('buttonRadius', v)} min={0} max={30} />
                                    <SliderRow canEdit={canEdit} label={t('survey.email_template.label_height')} value={fields.buttonPaddingV || '14'} onChange={(v) => updateField('buttonPaddingV', v)} min={6} max={30} />
                                    <SliderRow canEdit={canEdit} label={t('survey.email_template.label_width')} value={fields.buttonPaddingH || '36'} onChange={(v) => updateField('buttonPaddingH', v)} min={12} max={80} />
                                    <SliderRow canEdit={canEdit} label={t('survey.email_template.label_font_size')} value={fields.buttonFontSize || '16'} onChange={(v) => updateField('buttonFontSize', v)} min={10} max={28} />
                                </AccordionSection>

                                <AccordionSection title={t('survey.email_template.section_notes')} open={openSections.notes} onToggle={() => toggleSection('notes')}>
                                    <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: '#888' }}>{t('survey.email_template.label_security_note')}</div>
                                    <ColorRow canEdit={canEdit} label={t('survey.email_template.label_color')} value={fields.securityNoteColor || '#494C4E'} onChange={(v) => updateField('securityNoteColor', v)} />
                                    <SliderRow canEdit={canEdit} label={t('survey.email_template.label_font_size')} value={fields.securityNoteFontSize || '12'} onChange={(v) => updateField('securityNoteFontSize', v)} min={9} max={18} />
                                    <div style={{ marginBottom: '8px', marginTop: '6px', fontSize: '13px', fontWeight: '600', color: '#888' }}>{t('survey.email_template.label_assistance_note')}</div>
                                    <ColorRow canEdit={canEdit} label={t('survey.email_template.label_color')} value={fields.assistanceNoteColor || '#494C4E'} onChange={(v) => updateField('assistanceNoteColor', v)} />
                                    <SliderRow canEdit={canEdit} label={t('survey.email_template.label_font_size')} value={fields.assistanceNoteFontSize || '12'} onChange={(v) => updateField('assistanceNoteFontSize', v)} min={9} max={18} />
                                </AccordionSection>

                                <AccordionSection title={t('survey.email_template.section_signoff')} open={openSections.signoff} onToggle={() => toggleSection('signoff')}>
                                    <ColorRow canEdit={canEdit} label={t('survey.email_template.label_color')} value={fields.signoffColor || '#494C4E'} onChange={(v) => updateField('signoffColor', v)} />
                                    <SliderRow canEdit={canEdit} label={t('survey.email_template.label_font_size')} value={fields.signoffFontSize || '12'} onChange={(v) => updateField('signoffFontSize', v)} min={9} max={18} />
                                </AccordionSection>

                                <AccordionSection title={t('survey.email_template.section_footer')} open={openSections.footer} onToggle={() => toggleSection('footer')}>
                                    <ColorRow canEdit={canEdit} label={t('survey.email_template.label_color')} value={fields.footerColor || '#494C4E'} onChange={(v) => updateField('footerColor', v)} />
                                    <SliderRow canEdit={canEdit} label={t('survey.email_template.label_font_size')} value={fields.footerFontSize || '12'} onChange={(v) => updateField('footerFontSize', v)} min={9} max={18} />
                                    <ColorRow canEdit={canEdit} label={t('survey.email_template.label_border')} value={fields.footerBorderColor || '#C1C7CF'} onChange={(v) => updateField('footerBorderColor', v)} />
                                </AccordionSection>

                                <AccordionSection title={t('survey.email_template.section_placeholders')} open={openSections.placeholders} onToggle={() => toggleSection('placeholders')}>
                                    <div style={{ fontSize: '13px', color: '#666', lineHeight: '1.5', marginBottom: '10px', padding: '8px', background: '#f8f9fa', borderRadius: '4px', border: '1px solid #e9ecef' }}
                                        dangerouslySetInnerHTML={{ __html: t('survey.email_template.placeholder_desc') }}
                                    />
                                    {placeholders.map(p => (
                                        <div key={p.key} style={{ marginBottom: '10px' }}>
                                            <button
                                                onClick={() => copyPlaceholder(p.label)}
                                                style={{
                                                    display: 'inline-block', padding: '5px 12px',
                                                    background: '#E60000', color: '#fff', border: 'none',
                                                    borderRadius: '20px', fontSize: '13px', fontFamily: 'monospace',
                                                    cursor: 'pointer', fontWeight: '600',
                                                }}
                                                title={t('survey.email_template.click_to_copy')}
                                            >
                                                {p.label}
                                            </button>
                                            <div style={{ fontSize: '12px', color: '#888', marginTop: '3px', paddingLeft: '4px' }}>{p.desc}</div>
                                        </div>
                                    ))}
                                </AccordionSection>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showResetModal && (
                <div className="customvar-modal modal custom-modal" style={{ display: 'flex', position: 'fixed', inset: 0, zIndex: 9999, alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered" style={{ margin: 0 }}>
                        <div className="modal-content">
                            <div className="modal-body">
                                <div className="formvawrapper delete-modal-wrap modal-setting-medium">
                                    <div className="formstyle p-0">
                                        <div className="delete-body-wrap delete-language">
                                            <div className="heading">
                                                <h3>{t('survey.email_template.reset_title')}</h3>
                                                <p>
                                                    {t('survey.email_template.reset_warning')}
                                                </p>
                                            </div>
                                            <div className="button-wrap d-flex justify-content-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-delete"
                                                    onClick={handleResetConfirm}
                                                >
                                                    {t('survey.email_template.reset_confirm')}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-bordered color-cancel ms-3"
                                                    onClick={() => setShowResetModal(false)}
                                                >
                                                    {t('survey.email_template.reset_cancel')}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
